import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import type { DialogProps } from './Dialog.types';

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
};

/**
 * Reusable Dialog component with animations and overlay
 * 
 * Features:
 * - Fade-in/out animations
 * - Centered modal
 * - Backdrop overlay with click-to-close
 * - Escape key to close
 * - Body scroll lock when open
 * - Customizable header, content, and footer
 */
export const Dialog: React.FC<DialogProps> = ({
  open,
  onOpenChange,
  title,
  customHeader,
  children,
  footer,
  maxWidth = '2xl',
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  contentClassName = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  // Animation effect
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
      document.body.style.overflow = 'hidden';
    } else if (isVisible) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      document.body.style.overflow = '';
      return () => clearTimeout(timer);
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, isVisible]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    if (open) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onOpenChange]);

  if (!isVisible) return null;

  const handleOverlayClick = () => {
    onOpenChange(false);
  };

  const dialogContent = (
    <>
      {/* Overlay with fade animation */}
      <div
        className={`fixed inset-0 bg-black/70 z-[101] transition-opacity duration-200 ${overlayClassName}`}
        style={{ opacity: isAnimating ? 1 : 0 }}
        onClick={handleOverlayClick}
      />

      {/* Centered Dialog */}
      <div className="fixed inset-0 z-[102] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
        <div
          className={`bg-secondary rounded-t-2xl sm:rounded-2xl shadow-2xl w-full ${maxWidthClasses[maxWidth]} max-h-[90vh] sm:max-h-[85vh] flex flex-col pointer-events-auto transition-all duration-200 ${className}`}
          style={{
            opacity: isAnimating ? 1 : 0,
            transform: isAnimating ? 'scale(1)' : 'scale(0.95)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          {customHeader ? (
            customHeader
          ) : (title || showCloseButton) ? (
            <div className="flex-shrink-0 border-b border-border">
              <div className="flex items-center justify-between p-4">
                {title && (
                  <h2 className="text-xl font-bold text-text-primary">{title}</h2>
                )}
                {!title && <div />}
                {showCloseButton && (
                  <button
                    onClick={() => onOpenChange(false)}
                    className="p-2 hover:bg-primary rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-text-secondary" />
                  </button>
                )}
              </div>
            </div>
          ) : null}

          {/* Content - Scrollable */}
          <div className={`flex-1 overflow-y-auto ${contentClassName}`}>
            {children}
          </div>

          {/* Footer */}
          {footer && (
            <div className="flex-shrink-0 border-t border-border p-4">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(dialogContent, document.body);
};
